import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import reportWebVitals from './reportWebVitals'
import { BrowserRouter } from 'react-router-dom'
import ContextWrapper from '@/contexts'
import { AppThemeInit } from './theme/components/ThemeToggle'
import { Provider as ReduxProvider } from 'react-redux'
import { store } from '@/state'
import SupabaseUpdater from './components/Supabase/SupabaseUpdater'
import { initAmplitude } from './lib/amplitude/amplitudeAnalytics'
import './theme/styles/index.css'
import { AmplitudeUserUpdater } from './components/Amplitude/AmplitudeUserUpdater'
import { ChainUpdater } from './state/buyCrypto/updater'
import { SellCryptoChainUpdater } from './state/sellCrypto/updater'
import '@rainbow-me/rainbowkit/styles.css'
import {
  WagmiProvider,
  http,
  fallback,
  unstable_connector,
  createConfig
} from 'wagmi'
import {
  RainbowKitProvider,
  lightTheme,
  connectorsForWallets
} from '@rainbow-me/rainbowkit'
import {
  injectedWallet,
  metaMaskWallet,
  rabbyWallet,
  walletConnectWallet,
  okxWallet,
  trustWallet,
  binanceWallet,
  coinbaseWallet
} from '@rainbow-me/rainbowkit/wallets'
// import { binanceWallet } from '@/lib/wallets/binanceWallet'
import { mainnet, bsc, optimism, polygon } from 'wagmi/chains'
import { injected } from 'wagmi/connectors'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

const useStrictMode = process.env?.REACT_APP_USE_STRICT_MODE ?? 'true'
const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)

function Updaters() {
  return (
    <>
      <AppThemeInit />
      <ChainUpdater />
      <SellCryptoChainUpdater />
      <SupabaseUpdater />
      <AmplitudeUserUpdater />
    </>
  )
}

initAmplitude()

// add REACT_APP_WALLETCONNECT_PROJECT_ID in .env file
const projectId = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID!
const hasValidWalletConnectProjectId = projectId

const chains = [mainnet, bsc, optimism] as const

// RPC URLs from env (configurable per environment)
const ETH_RPC =
  process.env.REACT_APP_ETH_RPC_URL || 'https://ethereum-rpc.publicnode.com'
const BSC_RPC =
  process.env.REACT_APP_BSC_RPC_URL || 'https://bsc-dataseed.binance.org'
const OP_RPC = process.env.REACT_APP_OP_RPC_URL || 'https://mainnet.optimism.io'

const createWagmiConfig = () => {
  const connectors = connectorsForWallets(
    [
      {
        groupName: 'Recommended',
        wallets: [
          metaMaskWallet,
          rabbyWallet,
          walletConnectWallet,
          okxWallet,
          trustWallet,
          binanceWallet,
          coinbaseWallet
        ]
      },
      {
        groupName: 'Others',
        wallets: hasValidWalletConnectProjectId
          ? [injectedWallet, walletConnectWallet]
          : [injectedWallet]
      }
    ],
    {
      appName: 'KULAP Plus',
      projectId
    }
  )

  return createConfig({
    chains,
    transports: {
      [mainnet.id]: fallback([unstable_connector(injected), http(ETH_RPC)]),
      [bsc.id]: fallback([unstable_connector(injected), http(BSC_RPC)]),
      [optimism.id]: fallback([unstable_connector(injected), http(OP_RPC)])
    },
    connectors,
    ssr: false
  })
}

export const config = createWagmiConfig()

const rkThemeLight = lightTheme({
  borderRadius: 'medium',
  overlayBlur: 'small',
  accentColor: '#0559af'
})
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      retryDelay: 1000,
      staleTime: 30_000,
      refetchOnWindowFocus: false
    }
  }
})

root.render(
  useStrictMode.toLowerCase() === 'true' ? (
    <React.StrictMode>
      <ReduxProvider store={store}>
        <ContextWrapper>
          <WagmiProvider config={config}>
            <BrowserRouter>
              <QueryClientProvider client={queryClient}>
                <RainbowKitProvider theme={rkThemeLight} modalSize="compact">
                  <Updaters />
                  <App />
                </RainbowKitProvider>
              </QueryClientProvider>
            </BrowserRouter>
          </WagmiProvider>
        </ContextWrapper>
      </ReduxProvider>
    </React.StrictMode>
  ) : (
    <ReduxProvider store={store}>
      <ContextWrapper>
        <WagmiProvider config={config}>
          <BrowserRouter>
            <QueryClientProvider client={queryClient}>
              <RainbowKitProvider theme={rkThemeLight} modalSize="compact">
                <Updaters />
                <App />
              </RainbowKitProvider>
            </QueryClientProvider>
          </BrowserRouter>
        </WagmiProvider>
      </ContextWrapper>
    </ReduxProvider>
  )
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
